import { useEffect, useRef } from 'react';
import { getSocket } from '../../socket';
import { playReceivedSound } from '../../Utilities/soundEffects';

// ✅ Global counter to track hook initializations across the app lifecycle
let hookInstanceCounter = 0;

const useUniversalSocketHandlers = (
    currentUserId,
    activeChatId,
    setMessagesByChatId,
    setUnreadMap,
    setTypingMap
) => {
    const instanceId = useRef(++hookInstanceCounter);
    const seenMessageIds = useRef(new Set());

    useEffect(() => {
        const socket = getSocket();

        // 🛡️ Fix ESLint warning: Capture the current value of the ref 
        // in a local variable so it stays the same during cleanup.
        const currentHookId = instanceId.current;

        if (!socket || !currentUserId) {
            console.warn(`⚠️ [Hook #${currentHookId}] Setup skipped: No socket or UserID`);
            return;
        }

        console.log(`🔌 [Hook #${currentHookId}] MOUNTING: Listeners attached to Socket: ${socket.id}`);

        // --- 1. Handle Incoming Messages ---
        const onMessage = (message) => {
            const msgId = String(message._id);
            const chatId = String(message.chat || message.chatId);
            const senderId = String(message.sender?._id || message.sender);
            const isFromMe = senderId === String(currentUserId);

            console.log(`📩 [Hook #${currentHookId}] RECEIVED: "${message.content?.substring(0, 15)}..." from ${senderId}`);

            if (seenMessageIds.current.has(msgId)) {
                console.log(`♻️ [Hook #${currentHookId}] REJECTED DUPLICATE: ${msgId}`);
                return;
            }
            seenMessageIds.current.add(msgId);

            if (!isFromMe) {
                playReceivedSound();
            }

            setMessagesByChatId((prev) => {
                const existing = prev[chatId] || [];
                if (existing.some(m => String(m._id) === msgId)) return prev;

                return {
                    ...prev,
                    [chatId]: [...existing, { ...message, fromSelf: isFromMe }],
                };
            });

            if (!isFromMe && chatId !== String(activeChatId)) {
                console.log(`🔴 [Hook #${currentHookId}] UNREAD++ for Chat: ${chatId}`);
                setUnreadMap((prev) => ({
                    ...prev,
                    [chatId]: (prev[chatId] || 0) + 1,
                }));
            }
        };

        // --- 2. Handle Read Receipts ---
        const onReadAck = (ackData) => {
            console.log(`📖 [Hook #${currentHookId}] SEEN UPDATE:`, ackData);
            const { chatId, userId } = ackData;

            if (String(userId) === String(currentUserId)) {
                setUnreadMap((prev) => ({ ...prev, [chatId]: 0 }));
            }

            setMessagesByChatId((prev) => {
                const list = prev[chatId] || [];
                return {
                    ...prev,
                    [chatId]: list.map(m => ({ ...m, isRead: true }))
                };
            });
        };

        // --- 3. Handle Typing States ---
        const onTyping = (data) => {
            const chatId = String(data.chatId || data.chat);
            if (String(data.userId) === String(currentUserId)) return;
            setTypingMap((prev) => ({
                ...prev,
                [chatId]: { ...(prev[chatId] || {}), [data.userId]: data.username || 'Someone' },
            }));
        };

        const onStopTyping = (data) => {
            const chatId = String(data.chatId || data.chat);
            setTypingMap((prev) => {
                const chatTypers = { ...(prev[chatId] || {}) };
                delete chatTypers[data.userId];
                return { ...prev, [chatId]: chatTypers };
            });
        };

        // Attach Listeners
        socket.on('newMessage', onMessage);
        socket.on('messagesSeen', onReadAck);
        socket.on('typing', onTyping);
        socket.on('stopTyping', onStopTyping);
        socket.on('group-typing', onTyping);
        socket.on('group-stopTyping', onStopTyping);

        // --- Cleanup Logic ---
        return () => {
            console.log(`💀 [Hook #${currentHookId}] UNMOUNTING: Cleaning up listeners.`);
            socket.off('newMessage', onMessage);
            socket.off('messagesSeen', onReadAck);
            socket.off('typing', onTyping);
            socket.off('stopTyping', onStopTyping);
            socket.off('group-typing', onTyping);
            socket.off('group-stopTyping', onStopTyping);
        };
    }, [
        currentUserId,
        activeChatId,
        setMessagesByChatId,
        setUnreadMap,
        setTypingMap
    ]);

    return {
        onMessage: () => { },
        onReadAck: () => { },
        onTyping: () => { },
        onStopTyping: () => { }
    };
};

export default useUniversalSocketHandlers;